import { attachmentSchema } from "@/api/attachment";
import { multilingualFieldSchema, supportedLocales } from "@/i18n";
import { z } from "zod";
import { emptyToNullString } from "../types";

export const cmsPageTypes = [
  "catalogueRaisonne",
  "catalogueRaisonneGeneral",
  "selection",
  "news",
  "expertise",
  "aboutUs",
  "contact",
  "homepage",
  "login",
  "signup",
  "artist",
  "privacyPolicy",
  "legalNotices",
  "gcu",
] as const;

export const cmsPageTypeSchema = z.enum(cmsPageTypes);

export type CmsPageType = z.infer<typeof cmsPageTypeSchema>;

export interface CmsPageDto<C> {
  id: string;

  pageName: string;
  published: boolean;
  lang: (typeof supportedLocales)[number];
  pageBackgroundColorId?: string | null;
  pageTextColorMode?: "AUTO" | "THEME_TEXT" | "CUSTOM";
  pageTextColorId?: string | null;
  pageFontFamily?: string | null;
  pageFontVariant?: string | null;

  createdAt: Date;
  updatedAt: Date;

  type: CmsPageType;

  content: C;
}

export interface CreateCmsPage<C> {
  pageName: string;
  published: boolean;
  pageBackgroundColorId?: string | null;
  pageTextColorMode?: "AUTO" | "THEME_TEXT" | "CUSTOM";
  pageTextColorId?: string | null;
  pageFontFamily?: string | null;
  pageFontVariant?: string | null;
  content: C;
}

export interface UpdateCmsPage<C> {
  pageName: string;
  published?: boolean;
  pageBackgroundColorId?: string | null;
  pageTextColorMode?: "AUTO" | "THEME_TEXT" | "CUSTOM";
  pageTextColorId?: string | null;
  pageFontFamily?: string | null;
  pageFontVariant?: string | null;
  content: C;
}

export const CrTypeSchema = (p?: z.RawCreateParams) =>
  z.enum(["ARTWORKS", "MULTIPRINTS"], p);

export const cmsPageSchema = z.object({
  id: z.string(),
  pageName: z
    .string()
    .trim()
    .nonempty({ message: "cms.form.error.pageName.empty" }),
  published: z.boolean().default(false),
  pageType: cmsPageTypeSchema,
});

export const baseCmsFormSchema = cmsPageSchema.pick({
  pageName: true,
  published: true,
});
export type BaseCmsForm = z.infer<typeof baseCmsFormSchema>;

/** Base shape for locale form (mergeable). pageName is optional; use withPageNameRequiredWhenPublished after merge to validate. */
export const cmsPageLocaleBaseSchemaShape = cmsPageSchema
  .pick({ published: true })
  .extend({
    pageName: z.string().trim().optional(),
    pageBackgroundColorId: z.string().nullable().optional(),
    pageTextColorMode: z
      .enum(["AUTO", "THEME_TEXT", "CUSTOM"])
      .optional()
      .default("AUTO"),
    pageTextColorId: z.string().nullable().optional(),
    pageFontFamily: z.string().nullable().optional(),
    pageFontVariant: z.string().nullable().optional(),
  });

function pageNameRequiredWhenPublishedRefine(data: { published?: boolean; pageName?: string }, ctx: z.RefinementCtx) {
  if (data.published && !data.pageName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "cms.form.error.pageName.empty",
      path: ["pageName"],
    });
  }
}

/** pageName required only when published. Use this when the form has no extra content to merge (e.g. e-shop). */
export const cmsPageLocaleBaseSchema =
  cmsPageLocaleBaseSchemaShape.superRefine(pageNameRequiredWhenPublishedRefine);

/** Call after .merge(contentSchema) to require pageName when published. */
export function withPageNameRequiredWhenPublished<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine(pageNameRequiredWhenPublishedRefine) as unknown as T;
}

export const homepageContentSchema = z.object({
  id: z.string().optional(),

  websiteTitle: z
    .string()
    .trim()
    .nonempty({ message: "cms.form.error.pageName.empty" }),

  websiteSubtitle: emptyToNullString(),
  artistName: emptyToNullString(),
  birthDate: emptyToNullString(),
  deathDate: emptyToNullString(),
  credits: emptyToNullString(),

  catalogueRaisonneCTA: emptyToNullString(),

  resume: emptyToNullString(),
  footerArchives: emptyToNullString(),
});

export type HomepageContent = z.infer<typeof homepageContentSchema>;

export const artistContentSchema = z.object({
  id: z.string().optional(),
  credits: emptyToNullString(),
  resumeArtist: emptyToNullString(),

  biographySectionTitle: emptyToNullString(),
  biographyContent: emptyToNullString(),
  publishedBiography: z.boolean().optional().default(false),

  collectionsSectionTitle: emptyToNullString(),
  collectionsContent: emptyToNullString(),
  publishedCollections: z.boolean().optional().default(false),

  exhibitionsSectionTitle: emptyToNullString(),
  exhibitionsContent: emptyToNullString(),
  publishedExhibitions: z.boolean().optional().default(false),

  bibliographySectionTitle: emptyToNullString(),
  bibliographyContent: emptyToNullString(),
  publishedBibliography: z.boolean().optional().default(false),
});

export type ArtistContent = z.infer<typeof artistContentSchema>;

export const selectionContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});

export type SelectionContent = z.infer<typeof selectionContentSchema>;

export const newsItemSchema = z.object({
  id: z.string().optional(),
  title: multilingualFieldSchema(z.string()).optional(),
  content: multilingualFieldSchema(z.string()).optional(),
  excerpt: multilingualFieldSchema(z.string()).optional(),
  date: z.date(),
  status: z.enum(["DRAFT", "PUBLISHED"]).default("DRAFT"),

  attachments: z.array(attachmentSchema),

  updatedAt: z.string(),
  createdAt: z.string(),
});
export type NewsItem = z.infer<typeof newsItemSchema>;

export const newsPageContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  news: z.array(newsItemSchema),
});
export type NewsPageContent = z.infer<typeof newsPageContentSchema>;

export const newsPageEditContent = newsPageContentSchema.omit({
  news: true,
});

export type NewsPageEditContent = z.infer<typeof newsPageEditContent>;

export const expertiseContentSchema = z.object({
  id: z.string().optional(),

  title: emptyToNullString(),
  formDownloadCTA: emptyToNullString(),
  documentUrl: emptyToNullString(),
  content: emptyToNullString(),
});

export type ExpertiseContent = z.infer<typeof expertiseContentSchema>;

export const artworkAuthenticationContentSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  content: z.string().optional(),
  honorDeclaration: z.string().optional(),
  certificationLegalStatement: z.string().optional(),
  homePageFile: z.string().optional().nullable(),
  sidebarFile: z.string().optional().nullable(),
  sidebarTitle: z.string().optional().nullable(),
});
export type ArtworkAuthenticationContent = z.infer<
  typeof artworkAuthenticationContentSchema
>;

export const aboutUsContentSchema = z.object({
  id: z.string().optional(),

  content: emptyToNullString(),

  credits: emptyToNullString(),

  creditsTitle: emptyToNullString(),
  creditsContent: emptyToNullString(),
  publishedCredits: z.boolean().default(false),

  legalNoticesTitle: emptyToNullString(),
  legalNoticesContent: emptyToNullString(),
  publishedLegalNotices: z.boolean().default(false),
});

export type AboutUsContent = z.infer<typeof aboutUsContentSchema>;

export const contactContentSchema = z.object({
  id: z.string().optional(),

  // email: emptyToNullString(), // Formulaire contact masqué côté front
  content: emptyToNullString(),
  credits: emptyToNullString(),
});
export type ContactContent = z.infer<typeof contactContentSchema>;

export const loginContentSchema = z.object({
  id: z.string().optional(),
  subtitle: emptyToNullString(),
});
export type LoginContent = z.infer<typeof loginContentSchema>;

export const catalogueRaisonneGeneralContent = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  artworkCTA: emptyToNullString(),
  multipleCTA: emptyToNullString(),
  showPublicAuctions: z.boolean().optional().default(false),
});
export type CatalogueRaisonneGeneralContent = z.infer<
  typeof catalogueRaisonneGeneralContent
>;

export const catalogueRaisonneSpecificContent = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  runningTitle: emptyToNullString(),
  type: CrTypeSchema(),
  showRegistrationNumber: z.boolean().optional().default(false),
  /** Éviter `.default(true)` : zodResolver peut réappliquer le default et écraser un `false` saisi. */
  publishedTitle: z.boolean().optional(),
  publishedText: z.boolean().optional(),
  publishedImage: z.boolean().optional(),
});
export type CatalogueRaisonneSpecificContent = z.infer<
  typeof catalogueRaisonneSpecificContent
>;

export const signUpContentSchema = z.object({
  id: z.string().optional(),
  subtitle: emptyToNullString(),
});
export type SignUpContent = z.infer<typeof signUpContentSchema>;

export const privacyPolicyContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type PrivacyPolicyContent = z.infer<typeof privacyPolicyContentSchema>;

export const legalNoticesContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type LegalNoticesContent = z.infer<typeof legalNoticesContentSchema>;

export const gcuContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type GcuContent = z.infer<typeof gcuContentSchema>;

export const eshopContentSchema = z.object({
  id: z.string().optional(),
});
export type EShopContent = z.infer<typeof eshopContentSchema>;

/**
 * Shape runtime INCHANGÉE (les 4 locales requises) — le seed `{}` non typé du
 * reduce effaçait la forme inférée (type `{}` → `.fr`/`Object.values` KO).
 */
const requiredLocalesShape = supportedLocales.reduce(
  (acc, locale) => ({ ...acc, [locale]: z.string() }),
  {} as Record<(typeof supportedLocales)[number], z.ZodString>,
);

export const catalogueRaisonneSectionSchema = z.object({
  id: z.string(),
  nameMultilingual: z.object(requiredLocalesShape),
  contentMultilingual: z.object(requiredLocalesShape).optional(),
  titleMultilingual: z.object(requiredLocalesShape).optional(),
  showContent: z.boolean().optional(),
  order: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  cmsId: z.string(),
  type: z.string(),
  startValue: z.string().nullable(),
  endValue: z.string().nullable(),
  categoryId: z.string().nullable(),
  categoryMultilingual: multilingualFieldSchema(z.string()).optional(),
  target: CrTypeSchema(),
  vignette: attachmentSchema.nullable(),
  sortByInsideDecoupage: z.string().optional(),
});
export type CatalogueRaisonneSection = z.infer<
  typeof catalogueRaisonneSectionSchema
>;

export const catalogueRaisonneSectionUpsertSchema =
  catalogueRaisonneSectionSchema
    .pick({
      nameMultilingual: true,
      titleMultilingual: true,
      contentMultilingual: true,
      showContent: true,
      startValue: true,
      endValue: true,
      categoryId: true,
      categoryMultilingual: true,
      vignette: true,
      order: true,
      type: true,
      sortByInsideDecoupage: true,
    })
    // `id` optionnel : à préserver à la sauvegarde pour ne pas casser les
    // références Puck (`CatalogueDecoupage.decoupageId`, liens bouton, etc.).
    .extend({ id: z.string().optional() });

export type CatalogueRaisonneSectionUpsert = z.infer<
  typeof catalogueRaisonneSectionUpsertSchema
>;

export const socialTypes = [
  "FACEBOOK",
  "INSTAGRAM",
  "YOUTUBE",
  "LINKEDIN",
] as const;

export const socialTypeSchema = z.enum(socialTypes);

export type SocialType = z.infer<typeof socialTypeSchema>;

export const socialSchema = z.object({
  socialType: socialTypeSchema,
  url: z.string().nonempty({ message: "cms.form.error.url.empty" }),
  visible: z.boolean().default(false),
  colored: z.boolean().default(true),
});

export type Social = z.infer<typeof socialSchema>;

export const generalSettingsSchema = z
  .object({
    id: z.string().optional(),
    headerAlwaysVisible: z.boolean().default(false),
    headerAlwaysVisibleMobile: z.boolean().default(false),
    socials: z.array(socialSchema),
    isMixedCRMode: z.boolean().default(false),
    defaultWebsiteLanguage: z.enum(supportedLocales).default("fr"),
    websiteLanguages: z.array(z.enum(supportedLocales)).min(1),
    // Video / embed (YouTube/Vimeo) player settings
    playerAutoplay: z.boolean().default(false),
    playerControls: z.boolean().default(true),
    playerMuted: z.boolean().default(false),
    playerLoop: z.boolean().default(false),
    maintenanceEnabled: z.boolean().default(false),
    maintenanceMode: z.enum(["SIMPLE", "PUCK"]).optional(),
    // Contenu de la page simple, par langue : { [locale]: { title, line1, line2 } }
    maintenanceContent: z
      .record(
        z.object({
          title: z.string().default(""),
          line1: z.string().default(""),
          line2: z.string().default(""),
        }),
      )
      .nullable()
      .optional(),
    // URL signée de l'image (lecture seule, renvoyée par l'API)
    maintenanceImageUrl: z.string().nullable().optional(),
    maintenancePageId: z.string().nullable().optional(),
    abonnementPageId: z.string().nullable().optional(),
    selectionCategoryDetailPageId: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.websiteLanguages.includes(data.defaultWebsiteLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cms.generalSettings.error.defaultNotInLanguages",
        path: ["defaultWebsiteLanguage"],
      });
    }
  });

export type GeneralSettings = z.infer<typeof generalSettingsSchema>;

export const cmsColorSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1),
  hex: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: "cms.appearance.error.invalidHex" }),
  sortOrder: z.number().int().min(0),
});

export const cmsThemeStyleInputSchema = z.object({
  backgroundColorId: z.string().nullable().optional(),
  textColorId: z.string().nullable().optional(),
  separatorType: z.enum(["NONE", "SOLID", "DASHED", "DOTTED"]).optional(),
  separatorColorId: z.string().nullable().optional(),
  buttonColorId: z.string().nullable().optional(),
  fontFamily: z.string().nullable().optional(),
  fontVariant: z.string().nullable().optional(),
});

export const cmsThemeSchema = z.object({
  id: z.string().optional(),
  colors: z.array(cmsColorSchema),
  defaultPageBackgroundColorId: z.string().nullable().optional(),
  defaultPageTextColorId: z.string().nullable().optional(),
  defaultPageLinkColorId: z.string().nullable().optional(),
  defaultPageAltBackgroundColorId: z.string().nullable().optional(),
  defaultPageAltTextColorId: z.string().nullable().optional(),
  defaultPageFontFamily: z.string().nullable().optional(),
  defaultPageFontVariant: z.string().nullable().optional(),
  header: cmsThemeStyleInputSchema.optional(),
  footer: cmsThemeStyleInputSchema.optional(),
});

export type CmsThemeForm = z.infer<typeof cmsThemeSchema>;
